import * as React from 'react';
import type { TourPlacement, TourStep } from './types';

const { useEffect, useRef } = React;

const CARD_WIDTH = 320;
const GAP = 14;
const MARGIN = 8;
/** Matches the SBChat breakpoint so the tour and the chat agree on "mobile". */
const MOBILE_MAX = 768;

interface TourStepCardProps {
  step: TourStep;
  index: number;
  total: number;
  rect: DOMRect | null;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}

/** Places the card beside the anchor, flipping when it would leave the viewport. */
function position(rect: DOMRect | null, placement: TourPlacement): React.CSSProperties {
  const isNarrow = window.innerWidth <= MOBILE_MAX;
  if (isNarrow || !rect) {
    // Bottom sheet on phones, centered when there's nothing to point at.
    return isNarrow
      ? { left: MARGIN, right: MARGIN, bottom: MARGIN, width: 'auto' }
      : {
          left: Math.round(window.innerWidth / 2 - CARD_WIDTH / 2),
          top: Math.round(window.innerHeight / 2 - 90),
          width: CARD_WIDTH,
        };
  }

  const fitsBelow = rect.bottom + GAP + 180 < window.innerHeight;
  const fitsAbove = rect.top - GAP - 180 > 0;
  const fitsRight = rect.right + GAP + CARD_WIDTH < window.innerWidth;
  const fitsLeft = rect.left - GAP - CARD_WIDTH > 0;

  let resolved = placement;
  if (placement === 'bottom' && !fitsBelow) resolved = fitsAbove ? 'top' : 'right';
  if (placement === 'top' && !fitsAbove) resolved = fitsBelow ? 'bottom' : 'right';
  if (placement === 'right' && !fitsRight) resolved = fitsLeft ? 'left' : 'bottom';
  if (placement === 'left' && !fitsLeft) resolved = fitsRight ? 'right' : 'bottom';

  const clampLeft = (value: number) =>
    Math.max(MARGIN, Math.min(value, window.innerWidth - CARD_WIDTH - MARGIN));
  const clampTop = (value: number) => Math.max(MARGIN, Math.min(value, window.innerHeight - 200));

  switch (resolved) {
    case 'top':
      return { left: clampLeft(rect.left + rect.width / 2 - CARD_WIDTH / 2), top: rect.top - GAP, width: CARD_WIDTH, transform: 'translateY(-100%)' };
    case 'left':
      return { left: rect.left - GAP - CARD_WIDTH, top: clampTop(rect.top), width: CARD_WIDTH };
    case 'right':
      return { left: rect.right + GAP, top: clampTop(rect.top), width: CARD_WIDTH };
    case 'bottom':
    default:
      return { left: clampLeft(rect.left + rect.width / 2 - CARD_WIDTH / 2), top: rect.bottom + GAP, width: CARD_WIDTH };
  }
}

export const TourStepCard: React.FC<TourStepCardProps> = ({
  step, index, total, rect, onNext, onBack, onSkip,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const isLast = index === total - 1;

  // Move focus to the card on every step so keyboard and screen-reader users
  // follow along. Not a focus trap: the highlighted control stays reachable.
  useEffect(() => {
    cardRef.current?.focus();
  }, [step.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onSkip(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); onNext(); }
      else if (e.key === 'ArrowLeft' && index > 0) { e.preventDefault(); onBack(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onNext, onBack, onSkip, index]);

  return (
    <div
      ref={cardRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="false"
      aria-labelledby={`dd-tour-title-${step.id}`}
      aria-describedby={`dd-tour-body-${step.id}`}
      className="dd-tour__card"
      style={{ position: 'fixed', ...position(rect, step.placement || 'bottom') }}
    >
      <div className="dd-tour__progress" aria-live="polite">
        Step {index + 1} of {total}
      </div>
      <h2 className="dd-tour__title" id={`dd-tour-title-${step.id}`}>{step.title}</h2>
      <div className="dd-tour__body" id={`dd-tour-body-${step.id}`}>{step.body}</div>
      <div className="dd-tour__actions">
        <button type="button" className="dd-tour__skip" onClick={onSkip}>
          {isLast ? 'Dismiss' : 'Skip tour'}
        </button>
        <div className="dd-tour__nav">
          {index > 0 && (
            <button type="button" className="dd-tour__btn dd-tour__btn--ghost" onClick={onBack}>
              Back
            </button>
          )}
          <button type="button" className="dd-tour__btn dd-tour__btn--primary" onClick={onNext}>
            {isLast ? 'Got it' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TourStepCard;
